import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import EditDossierInscriptionModal from "./EditDossierInscriptionModal";
import CreateDossierInscriptionModal from "./CreateDossierInscriptionModal";
import formatDate from "./../../../utils/formatDate";
import { GoFileSymlinkFile } from "react-icons/go";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import Button from "./../../../components/Button/Button";
import ButtonEdit from "./../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import {
  FaPlusSquare,
  FaGoogleDrive,
  FaServer,
  FaExternalLinkAlt,
  FaFolderPlus,
  FaFolderOpen,
} from "react-icons/fa";
import {
  getAllDossiersInscription,
  deleteDossierInscription,
  hasAdherentFolder,
  ensureAdherentFolder,
  updateDossierInscription,
} from "./../../../services/dossierServices";
import { getSettings } from "./../../../services/settingsServices";
import { getGroupsList } from "../../../services/groupServices";
import { sendNotificationToAdmin } from "./../../../services/notificationServices";
import useScrollToTop from "../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import "react-toastify/dist/ReactToastify.css";
import "./DossierInscriptionPage.css";

const API_BASE = process.env.REACT_APP_API_URL || "";

/* ---------- helpers type de lien ---------- */
const isDrive = (u = "") =>
  /^https?:\/\/(drive\.google\.com|docs\.google\.com)\//i.test(u);

const isServerFile = (u = "") =>
  u.startsWith("/uploads/") || u.startsWith(`${API_BASE}/uploads/`);

// ----- utilitaire: exécuter des tâches avec une concurrence limitée -----
const runWithConcurrency = async (items, worker, limit = 6) => {
  const results = new Array(items.length);
  let i = 0;
  const runners = new Array(Math.min(limit, items.length))
    .fill(0)
    .map(async () => {
      while (i < items.length) {
        const idx = i++;
        results[idx] = await worker(items[idx], idx);
      }
    });
  await Promise.all(runners);
  return results;
};

const toAbsolute = (u = "") => {
  if (!u) return u;
  if (u.startsWith("/")) return `${API_BASE}${u}`;
  return u;
};

/* Cellule “fichier” avec icône de type + titre explicite */
function FileCell({ url, title }) {
  if (!url) return <span className="text-slate-400">—</span>;

  const abs = toAbsolute(url);
  const drive = isDrive(url);
  const server = isServerFile(url);

  return (
    <a
      href={abs}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 group"
      title={title}
    >
      {drive ? (
        <FaGoogleDrive className="h-5 w-5 text-emerald-600 group-hover:opacity-80" />
      ) : server ? (
        <FaServer className="h-5 w-5 text-indigo-600 group-hover:opacity-80" />
      ) : (
        <FaExternalLinkAlt className="h-4 w-4 text-slate-700 group-hover:opacity-80" />
      )}
      <span className="sr-only">{title}</span>
    </a>
  );
}

export default function DossierInscriptionPage() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtres
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [validateCmFilter, setValidateCmFilter] = useState("");
  const [validateLicenceFilter, setValidateLicenceFilter] = useState("");
  const [validateReglementFilter, setValidateReglementFilter] = useState("");
  const [seasonFilter, setSeasonFilter] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");

  // Modales
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editableDossier, setEditableDossier] = useState(null);

  // Drapeaux dossier FS par adhérent: { [adherentId]: boolean }
  const [fsFlags, setFsFlags] = useState({});

  // Alerte doublons
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  const scrollRef = useRef(null);

  // Pour ne pas requêter 2x le même adherentId (fs flags)
  const fetchedFsRef = useRef(new Set()); // déjà récupéré (OK ou erreur)
  const fetchingFsRef = useRef(new Set()); // en cours de récupération

  async function fetchFsFlagsFor(adherentIds = []) {
    const todo = adherentIds.filter(
      (id) =>
        id && !fetchedFsRef.current.has(id) && !fetchingFsRef.current.has(id),
    );
    if (!todo.length) return;

    todo.forEach((id) => fetchingFsRef.current.add(id));

    try {
      await runWithConcurrency(
        todo,
        async (id) => {
          try {
            const { exists } = await hasAdherentFolder(id);
            setFsFlags((p) => ({ ...p, [id]: !!exists }));
          } catch {
            // silencieux
          } finally {
            fetchingFsRef.current.delete(id);
            fetchedFsRef.current.add(id);
          }
        },
        6,
      );
    } finally {
      // noop
    }
  }

  // Flags de sauvegarde pour les toggles
  const [savingFlags, setSavingFlags] = useState({});

  const isSaving = (id, field) => !!savingFlags[`${id}:${field}`];
  const setSavingFlag = (id, field, enabled) => {
    const key = `${id}:${field}`;
    setSavingFlags((prev) =>
      enabled
        ? { ...prev, [key]: true }
        : (() => {
            const copy = { ...prev };
            delete copy[key];
            return copy;
          })(),
    );
  };

  // Met à jour une seule ligne localement
  const patchDossierRow = (id, patch) => {
    setDossiers((list) =>
      list.map((d) => (d.id === id ? { ...d, ...patch } : d)),
    );
  };

  // Recharger uniquement la ligne depuis le serveur (robuste si l’API renvoie un objet partiel)
  const refreshOneRowFromServer = useCallback(async (id) => {
    try {
      // on préserve la position de scroll pour éviter les “sauts”
      const restoreScroll = (() => {
        const top = scrollRef.current?.scrollTop ?? 0;
        return () => {
          if (scrollRef.current) scrollRef.current.scrollTop = top;
        };
      })();

      const data = await getAllDossiersInscription();
      if (Array.isArray(data)) {
        const fresh = data.find((d) => d.id === id);
        if (fresh) {
          patchDossierRow(id, fresh);
        }
      }
      restoreScroll();
    } catch {
      // silencieux : on garde l’optimistic update
    }
  }, []);

  // Date YYYY-MM-DD
  const todayISO = () => new Date().toISOString().slice(0, 10);

  const formatDateFr = () => {
    return new Date().toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getAdminName = () => {
    const prenom = String(user?.prenom || "").trim();
    const nom = String(user?.nom || "")
      .trim()
      .toUpperCase();

    return `${prenom} ${nom}`.trim() || "un administrateur";
  };

  const getAdherentFullName = (dossier) => {
    const adherent = dossier?.Adherent || {};
    const nom = String(adherent?.nom || "")
      .trim()
      .toUpperCase();
    const prenom = String(adherent?.prenom || "").trim();

    return `${nom} ${prenom}`.trim() || "l'adhérent";
  };

  const buildValidationNotification = (dossier, field) => {
    const adherentName = getAdherentFullName(dossier);
    const date = formatDateFr();
    const adminName = getAdminName();

    if (field === "validate_licence") {
      return `Licence de ${adherentName} validée le ${date} par ${adminName}.`;
    }

    if (field === "validate_reglement") {
      return `Règlement de ${adherentName} validé le ${date} par ${adminName}.`;
    }

    return "";
  };

  const notifyAdminValidation = async (dossier, field) => {
    if (!["validate_licence", "validate_reglement"].includes(field)) return;

    const token = user?.token || localStorage.getItem("token");
    const message = buildValidationNotification(dossier, field);

    if (!token || !message) {
      console.warn(
        "Notification admin non envoyée : token ou message manquant.",
      );
      return;
    }

    try {
      await sendNotificationToAdmin(message, token);
    } catch (error) {
      console.error("Erreur notification admin validation dossier :", error);
    }
  };

  /**
   * Toggle d’un champ booléen: "validate_cm" | "validate_licence" | "validate_reglement"
   * Optimistic update immédiat + rafraîchissement de la ligne depuis le serveur après succès.
   * Pour "validate_cm", on gère aussi "date_cm".
   */
  const toggleValidate = async (dossier, field, label) => {
    if (!dossier?.id) return;

    const id = dossier.id;
    const next = !dossier[field];

    const payload = { [field]: next };

    if (field === "validate_cm") {
      if (next) {
        if (!dossier?.date_cm) {
          payload.date_cm = todayISO();
        }
      } else {
        payload.date_cm = null;
      }
    }

    setSavingFlag(id, field, true);

    try {
      await updateDossierInscription(id, payload);

      if (next && ["validate_licence", "validate_reglement"].includes(field)) {
        await notifyAdminValidation(dossier, field);
      }

      await fetchDossiersSilent();

      toast.success(`${label} mis à jour (${next ? "Oui" : "Non"})`);
    } catch (e) {
      console.error(e);
      toast.error(`Impossible de mettre à jour ${label}.`);
    } finally {
      setSavingFlag(id, field, false);
    }
  };

  // Debounce recherche
  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(searchTerm.trim().toLowerCase()),
      250,
    );
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchDossiers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllDossiersInscription();
      const list = Array.isArray(data) ? data : [];
      setDossiers(list);
      checkForDuplicates(list);
    } catch (error) {
      console.error("Erreur lors du chargement des dossiers:", error);
      toast.error("Erreur lors du chargement des dossiers d'inscription.");
    } finally {
      setLoading(false);
    }
  }, []);

  const hydrateFsFlags = useCallback(async (list) => {
    try {
      const entries = await Promise.all(
        (list || []).map(async (d) => {
          const adherentId = d?.inscription_adherent_id;
          if (!adherentId) return [adherentId, false];
          try {
            const { exists } = await hasAdherentFolder(adherentId);
            return [adherentId, !!exists];
          } catch {
            return [adherentId, false];
          }
        }),
      );
      setFsFlags(Object.fromEntries(entries));
    } catch (e) {
      // silencieux
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const settings = await getSettings();
      if (settings && settings.length > 0) {
        const activeSeason = settings[0].saison_active;
        const previousSeason = getPreviousSeason(activeSeason);
        const nextSeason = getNextSeason(activeSeason);
        setSeasons([previousSeason, activeSeason, nextSeason]);
        setSeasonFilter(activeSeason);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des paramètres:", error);
    }
  }, []);

  const fetchGroupsData = useCallback(async () => {
    try {
      const groupsList = await getGroupsList();
      setGroups(Array.isArray(groupsList) ? groupsList : []);
    } catch (error) {
      console.error("Erreur lors du chargement des groupes:", error);
    }
  }, []);

  useEffect(() => {
    fetchDossiers();
    fetchSettings();
    fetchGroupsData();
  }, [fetchDossiers, fetchSettings, fetchGroupsData]);

  const fetchDossiersSilent = useCallback(async () => {
    try {
      const data = await getAllDossiersInscription();
      const listRaw = Array.isArray(data) ? data : [];
      // petite normalisation défensive pour éviter tout crash de rendu
      const list = listRaw.map((d) => ({
        ...d,
        Adherent: {
          ...(d.Adherent || {}),
          Groups: Array.isArray(d?.Adherent?.Groups) ? d.Adherent.Groups : [],
        },
        validate_cm: !!d.validate_cm,
        validate_licence: !!d.validate_licence,
        validate_reglement: !!d.validate_reglement,
        date_cm: d.date_cm || null,
      }));
      setDossiers(list);
      checkForDuplicates(list);
    } catch (error) {
      console.error("Refetch silencieux en erreur:", error);
      toast.error("Erreur lors du rechargement des dossiers (refetch).");
    }
  }, []);

  const [rowRefreshing, setRowRefreshing] = useState({});
  const setRowRefreshingFlag = (id, enabled) =>
    setRowRefreshing((prev) => ({ ...prev, [id]: !!enabled }));
  const isRowRefreshing = (id) => !!rowRefreshing[id];

  const getPreviousSeason = (currentSeason) => {
    if (!currentSeason) return "";
    const [startYear, endYear] = currentSeason.split("-").map(Number);
    return `${startYear - 1}-${endYear - 1}`;
  };
  const getNextSeason = (currentSeason) => {
    if (!currentSeason) return "";
    const [startYear, endYear] = currentSeason.split("-").map(Number);
    return `${startYear + 1}-${endYear + 1}`;
  };

  // Doublons
  const checkForDuplicates = (data) => {
    const names = data.map(
      (d) =>
        `${d?.Adherent?.nom?.toLowerCase() || ""} ${
          d?.Adherent?.prenom?.toLowerCase() || ""
        }`,
    );
    const seen = new Set();
    const duplicates = [];
    for (const n of names) {
      if (seen.has(n)) duplicates.push(n);
      else seen.add(n);
    }
    setDuplicateWarning(
      duplicates.length > 0
        ? `Attention, doublon détecté : ${Array.from(new Set(duplicates)).join(
            ", ",
          )}`
        : null,
    );
  };

  // Filtrage mémoïsé
  const filtered = useMemo(() => {
    return dossiers.filter((dossier) => {
      const a = dossier?.Adherent || {};
      const matchAdherent =
        !debouncedSearch ||
        `${a.nom || ""} ${a.prenom || ""}`
          .toLowerCase()
          .includes(debouncedSearch);

      const matchValidateCm = validateCmFilter
        ? String(!!dossier?.validate_cm) === validateCmFilter
        : true;

      const matchValidateLicence = validateLicenceFilter
        ? String(!!dossier?.validate_licence) === validateLicenceFilter
        : true;

      const matchValidateReglement = validateReglementFilter
        ? String(!!dossier?.validate_reglement) === validateReglementFilter
        : true;

      const matchSeason = seasonFilter
        ? a?.saison_active === seasonFilter
        : true;

      const matchGroup =
        selectedGroup === "" ||
        (a?.Groups &&
          a.Groups.some((g) => String(g?.id) === String(selectedGroup)));

      return (
        matchAdherent &&
        matchValidateCm &&
        matchValidateLicence &&
        matchValidateReglement &&
        matchSeason &&
        matchGroup
      );
    });
  }, [
    dossiers,
    debouncedSearch,
    validateCmFilter,
    validateLicenceFilter,
    validateReglementFilter,
    seasonFilter,
    selectedGroup,
  ]);

  // Charger les fsFlags pour toutes les lignes affichées (sans pagination)
  useEffect(() => {
    const ids = filtered.map((d) => d?.inscription_adherent_id).filter(Boolean);
    fetchFsFlagsFor(ids);
  }, [filtered]);

  const openEditModal = (dossier) => {
    setEditableDossier(dossier);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => setIsEditModalOpen(false);

  const deleteDossier = async (id) => {
    if (!window.confirm("Supprimer ce dossier d'inscription ?")) return;
    try {
      await deleteDossierInscription(id);
      toast.success("Dossier supprimé avec succès.");
      fetchDossiers();
    } catch (error) {
      console.error("Erreur lors de la suppression du dossier", error);
      toast.error("Erreur lors de la suppression du dossier.");
    }
  };

  // Création du dossier FS pour un adhérent (ligne du tableau)
  const handleEnsureFolder = async (adherentId) => {
    try {
      const res = await ensureAdherentFolder(adherentId);
      if (res?.created) {
        toast.success("Dossier serveur créé.");
      } else {
        toast.info("Le dossier existait déjà.");
      }
      setFsFlags((p) => ({ ...p, [adherentId]: true }));
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la création du dossier serveur.");
    }
  };

  // Export CSV (inchangé)
  const exportCSV = () => {
    const header = [
      "Nom",
      "Prénom",
      "Saison",
      "CM valide",
      "Date CM",
      "Licence valide",
      "Règlement valide",
      "Groupes",
      "URL CM",
      "URL Formulaire à signer",
      "URL Formulaire signé",
      "URL Licence",
      "Lien Drive",
    ];

    const rows = filtered.map((d) => {
      const a = d?.Adherent || {};
      const groups = (a?.Groups || []).map((g) => g?.nom).join(", ");
      return [
        a?.nom || "",
        a?.prenom || "",
        a?.saison_active || "",
        d?.validate_cm ? "Oui" : "Non",
        d?.date_cm ? formatDate(d.date_cm) : "",
        d?.validate_licence ? "Oui" : "Non",
        d?.validate_reglement ? "Oui" : "Non",
        groups,
        d?.cm_url || "",
        d?.formulaire_licence_a_signer || "",
        d?.formulaire_licence_signe || "",
        d?.licence_url || "",
        d?.lien_drive || "",
      ]
        .map((s) => `"${String(s).replace(/"/g, '""')}"`)
        .join(";");
    });

    const csv = [header.join(";"), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dossiers_inscription.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Administrateur"
          title="Dossiers d'inscription"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau dégradé + actions */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Suivi des dossiers d’inscription (Certificat Médical,
                Licence-Assurance, Règlement)
              </h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                Filtrez par <span className="font-semibold">saison</span>,{" "}
                <span className="font-semibold">validation</span> et{" "}
                <span className="font-semibold">groupe</span>. Première colonne
                et entête collantes pour naviguer rapidement dans de grands
                tableaux.
              </p>

              {/* Actions */}
              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                <Button
                  text="Ajouter un dossier d'inscription"
                  icon={FaPlusSquare}
                  onClick={() => setIsCreateModalOpen(true)}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-2"
                />
                <Button
                  text="Exporter CSV (filtres)"
                  onClick={exportCSV}
                  className="!bg-white/90 !text-slate-900 !hover:bg-white !rounded-xl !shadow-md"
                />
              </div>

              {duplicateWarning && (
                <div className="mt-4 max-w-4xl mx-auto rounded-xl bg-rose-600/90 px-4 py-2 text-sm ring-1 ring-white/40">
                  {duplicateWarning}
                </div>
              )}
            </div>
          </div>

          {/* Toolbar filtres */}
          <div className="px-5 py-5 sm:px-8">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <input
                type="text"
                placeholder="Rechercher (nom, prénom)…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />

              <select
                value={validateCmFilter}
                onChange={(e) => setValidateCmFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">CM (tous)</option>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </select>

              <select
                value={validateLicenceFilter}
                onChange={(e) => setValidateLicenceFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Licence (tous)</option>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </select>

              <select
                value={validateReglementFilter}
                onChange={(e) => setValidateReglementFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Règlement (tous)</option>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </select>

              <select
                value={seasonFilter}
                onChange={(e) => setSeasonFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Saison (toutes)</option>
                {seasons.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Tous les groupes</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.nom}
                  </option>
                ))}
              </select>
            </div>

            {/* Compteur entre filtres et tableau */}
            <div className="mt-3 text-sm text-slate-700 dark:text-slate-200">
              Adhérents affichés :{" "}
              <span className="font-semibold">{filtered.length}</span>
            </div>
          </div>

          {/* Contenu */}
          <div className="px-5 pb-6 sm:px-8">
            {/* Skeleton */}
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            )}

            {/* Empty */}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun dossier
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajoutez un dossier ou modifiez vos filtres.
                </p>
              </div>
            )}

            {/* Table */}
            {!loading && filtered.length > 0 && (
              <div
                ref={scrollRef}
                className="overflow-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 max-h-[72vh]"
              >
                <table className="min-w-[1200px] w-full">
                  <thead className="sticky top-0 z-20">
                    <tr className="bg-slate-50/90 dark:bg-slate-900/90 backdrop-blur uppercase tracking-wide text-xs">
                      {[
                        "Adhérent",
                        "FS",
                        "Lien CM",
                        "Date CM",
                        "CM",
                        "Form.",
                        "Form. Signé",
                        "Licence",
                        "LIC",
                        "REG",
                        "Drive",
                        "Groupe",
                        "Actions",
                      ].map((h, idx) => (
                        <th
                          key={h}
                          className={`px-4 py-3 text-left font-semibold text-slate-700 dark:text-white ${
                            idx === 0
                              ? "sticky left-0 z-20 bg-slate-50/90 dark:bg-slate-900/90 backdrop-blur"
                              : ""
                          }`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                    {filtered.map((dossier) => {
                      const a = dossier?.Adherent || {};
                      const adherentId = dossier?.inscription_adherent_id;
                      const fsOk = fsFlags[adherentId];

                      return (
                        <tr
                          key={dossier.id}
                          className="transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                        >
                          {/* Adhérent (colonne collante) */}
                          <td className="px-4 py-3 sticky left-0 z-10 bg-white/90 dark:bg-slate-900/40">
                            <div className="font-semibold text-slate-900 dark:text-slate-100">
                              {a.nom || "—"}
                            </div>
                            <div className="text-slate-600 dark:text-slate-300">
                              {a.prenom || "—"}
                            </div>
                          </td>

                          {/* FS folder status */}
                          <td className="px-4 py-3">
                            {fsOk ? (
                              <span
                                className="inline-flex items-center gap-1 rounded-md bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 px-2 py-0.5 text-xs font-semibold"
                                title="Dossier serveur présent"
                              >
                                <FaFolderOpen />
                                OK
                              </span>
                            ) : (
                              <button
                                onClick={() => handleEnsureFolder(adherentId)}
                                className="inline-flex items-center gap-1 rounded-md bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200 px-2 py-0.5 text-xs font-semibold hover:opacity-90"
                                title="Créer le dossier sur le serveur"
                              >
                                <FaFolderPlus />
                                Créer
                              </button>
                            )}
                          </td>

                          {/* Lien CM */}
                          <td className="px-4 py-3">
                            <FileCell
                              url={dossier.cm_url}
                              title="Ouvrir le certificat médical"
                            />
                          </td>

                          {/* Date CM */}
                          <td className="px-4 py-3 text-xs">
                            {dossier.date_cm
                              ? formatDate(dossier.date_cm)
                              : "—"}
                          </td>

                          {/* CM valide ? */}
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() =>
                                toggleValidate(dossier, "validate_cm", "CM")
                              }
                              disabled={isSaving(dossier.id, "validate_cm")}
                              className={`inline-flex items-center justify-center rounded-md px-2.5 py-1 text-sm font-semibold ring-1 transition
      ${
        dossier.validate_cm
          ? "bg-emerald-100 text-emerald-700 ring-emerald-200 hover:bg-emerald-200"
          : "bg-rose-100 text-rose-700 ring-rose-200 hover:bg-rose-200"
      }
      ${
        isSaving(dossier.id, "validate_cm")
          ? "opacity-60 pointer-events-none"
          : ""
      }`}
                              title={`Basculer CM (${
                                dossier.validate_cm ? "Oui" : "Non"
                              })`}
                              aria-label={`Basculer CM (${
                                dossier.validate_cm ? "Oui" : "Non"
                              })`}
                            >
                              {dossier.validate_cm ? "✅" : "❌"}
                              <span className="ml-1">
                                {dossier.validate_cm ? "Oui" : "Non"}
                              </span>
                            </button>
                          </td>

                          {/* Formulaire à signer */}
                          <td className="px-4 py-3">
                            <FileCell
                              url={dossier.formulaire_licence_a_signer}
                              title="Ouvrir le formulaire à signer"
                            />
                          </td>

                          {/* Formulaire signé */}
                          <td className="px-4 py-3">
                            <FileCell
                              url={
                                dossier.formulaire_licence_signe ||
                                dossier.formulaire_licence_signee
                              }
                              title="Ouvrir le formulaire signé"
                            />
                          </td>

                          {/* Licence */}
                          <td className="px-4 py-3">
                            <FileCell
                              url={
                                dossier.licence_url ||
                                dossier.licence_assurance_url
                              }
                              title="Ouvrir la licence"
                            />
                          </td>

                          {/* LIC validé ? */}
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() =>
                                toggleValidate(
                                  dossier,
                                  "validate_licence",
                                  "Licence",
                                )
                              }
                              disabled={isSaving(
                                dossier.id,
                                "validate_licence",
                              )}
                              className={`inline-flex items-center justify-center rounded-md px-2.5 py-1 text-sm font-semibold ring-1 transition
      ${
        dossier.validate_licence
          ? "bg-emerald-100 text-emerald-700 ring-emerald-200 hover:bg-emerald-200"
          : "bg-rose-100 text-rose-700 ring-rose-200 hover:bg-rose-200"
      }
      ${
        isSaving(dossier.id, "validate_licence")
          ? "opacity-60 pointer-events-none"
          : ""
      }`}
                              title={`Basculer Licence (${
                                dossier.validate_licence ? "Oui" : "Non"
                              })`}
                              aria-label={`Basculer Licence (${
                                dossier.validate_licence ? "Oui" : "Non"
                              })`}
                            >
                              {dossier.validate_licence ? "✅" : "❌"}
                              <span className="ml-1">
                                {dossier.validate_licence ? "Oui" : "Non"}
                              </span>
                            </button>
                          </td>

                          {/* REG validé ? */}
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() =>
                                toggleValidate(
                                  dossier,
                                  "validate_reglement",
                                  "Règlement",
                                )
                              }
                              disabled={isSaving(
                                dossier.id,
                                "validate_reglement",
                              )}
                              className={`inline-flex items-center justify-center rounded-md px-2.5 py-1 text-sm font-semibold ring-1 transition
      ${
        dossier.validate_reglement
          ? "bg-emerald-100 text-emerald-700 ring-emerald-200 hover:bg-emerald-200"
          : "bg-rose-100 text-rose-700 ring-rose-200 hover:bg-rose-200"
      }
      ${
        isSaving(dossier.id, "validate_reglement")
          ? "opacity-60 pointer-events-none"
          : ""
      }`}
                              title={`Basculer Règlement (${
                                dossier.validate_reglement ? "Oui" : "Non"
                              })`}
                              aria-label={`Basculer Règlement (${
                                dossier.validate_reglement ? "Oui" : "Non"
                              })`}
                            >
                              {dossier.validate_reglement ? "✅" : "❌"}
                              <span className="ml-1">
                                {dossier.validate_reglement ? "Oui" : "Non"}
                              </span>
                            </button>
                          </td>

                          {/* Dossier Drive */}
                          <td className="px-4 py-3">
                            <FileCell
                              url={dossier.lien_drive}
                              title="Ouvrir le dossier Drive"
                            />
                          </td>

                          {/* Groupes */}
                          <td className="px-4 py-3 text-xs">
                            {a?.Groups?.length
                              ? a.Groups.map((g) => g?.nom).join(", ")
                              : "N/A"}
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <ButtonEdit
                                onClick={() => openEditModal(dossier)}
                              />
                              <ButtonDelete
                                onClick={() => deleteDossier(dossier.id)}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>

      {/* Modales */}
      <EditDossierInscriptionModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        dossier={editableDossier}
        refreshList={fetchDossiers}
      />
      <CreateDossierInscriptionModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        refreshList={fetchDossiers}
        currentUser={user}
      />
    </section>
  );
}
